import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import AppImage from '../../components/AppImage';
import Icon from '../../components/AppIcon';
import { ShoppingCart, Trash2, Plus, Minus, Heart, Tag, Lock, Truck, AlertCircle } from 'lucide-react';
import notificationService from '../../utils/notificationService';

export default function ShoppingCartManagement() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { cartItems, cartTotal, updateQuantity, removeFromCart, clearCart } = useCart();
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [savedForLater, setSavedForLater] = useState([]);
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);

  // Calculate shipping (free over 500 TL)
  const calculateShipping = () => {
    return cartTotal >= 500 ? 0 : 50;
  };

  // Calculate tax (KDV 20%)
  const calculateTax = () => {
    return cartTotal * 0.20;
  };

  // Calculate discount from coupon
  const calculateDiscount = () => {
    if (!appliedCoupon) return 0;
    if (appliedCoupon?.type === 'percentage') {
      return cartTotal * (appliedCoupon?.value / 100);
    }
    return appliedCoupon?.value;
  };

  // Calculate final total
  const calculateFinalTotal = () => {
    const subtotal = cartTotal;
    const shipping = calculateShipping();
    const tax = calculateTax();
    const discount = calculateDiscount();
    return subtotal + shipping + tax - discount;
  };

  // Handle coupon application
  const handleApplyCoupon = async () => {
    if (!couponCode?.trim()) {
      notificationService?.error('Lütfen bir kupon kodu girin');
      return;
    }

    setIsApplyingCoupon(true);
    
    // Simulate coupon validation (in real app, this would be API call)
    await new Promise(resolve => setTimeout(resolve, 800));
    
    // Mock coupon validation
    const mockCoupons = {
      'WELCOME10': { type: 'percentage', value: 10, description: '10% İndirim' },
      'FREESHIP': { type: 'shipping', value: 50, description: 'Ücretsiz Kargo' },
      'SAVE50': { type: 'fixed', value: 50, description: '50 TL İndirim' }
    };

    const coupon = mockCoupons?.[couponCode?.toUpperCase()];
    
    if (coupon) {
      setAppliedCoupon({ ...coupon, code: couponCode?.toUpperCase() });
      notificationService?.success(`Kupon uygulandı: ${coupon?.description}`);
    } else {
      notificationService?.error('Geçersiz kupon kodu');
    }
    
    setIsApplyingCoupon(false);
  };

  // Handle remove coupon
  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    notificationService?.info('Kupon kaldırıldı');
  };

  // Handle save for later
  const handleSaveForLater = (item) => {
    setSavedForLater(prev => [...prev, item]);
    removeFromCart(item?.id);
    notificationService?.success('Ürün daha sonra için kaydedildi');
  };

  // Handle move to cart
  const handleMoveToCart = (item) => {
    setSavedForLater(prev => prev?.filter(i => i?.id !== item?.id));
    // Would call addToCart here, but we need to refetch product data
    notificationService?.success('Ürün sepete eklendi');
  };

  // Handle quantity update with stock validation
  const handleQuantityUpdate = (productId, change) => {
    const item = cartItems?.find(i => i?.id === productId);
    if (!item) return;

    const newQuantity = item?.quantity + change;
    
    if (newQuantity < 1) {
      notificationService?.warning('Miktar 1\'den az olamaz');
      return;
    }

    if (item?.trackInventory && item?.stockQuantity && newQuantity > item?.stockQuantity) {
      notificationService?.warning(`Stokta sadece ${item?.stockQuantity} adet mevcut`);
      return;
    }

    updateQuantity(productId, change);
  };

  // Handle checkout
  const handleCheckout = () => {
    if (!user) {
      notificationService?.info('Ödeme için giriş yapmalısınız');
      navigate('/login', { state: { from: '/shopping-cart-management' } });
      return;
    }

    if (cartItems?.length === 0) {
      notificationService?.warning('Sepetiniz boş');
      return;
    }

    navigate('/advanced-checkout-system', { state: { appliedCoupon } });
  };

  if (cartItems?.length === 0 && savedForLater?.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gray-100 rounded-full mb-6">
              <ShoppingCart className="h-12 w-12 text-gray-400" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Sepetiniz Boş</h2>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              Alışverişe başlamak için resmi mağazamızı ziyaret edin ve ürünleri keşfedin
            </p>
            <Button
              onClick={() => navigate('/official-store')}
              size="lg"
              className="inline-flex items-center gap-2"
            >
              <ShoppingCart className="h-5 w-5" />
              Alışverişe Başla
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Alışveriş Sepeti</h1>
          <p className="text-gray-600">
            {cartItems?.length} ürün sepetinizde
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items Section */}
          <div className="lg:col-span-2 space-y-4">
            {/* Cart Items */}
            {cartItems?.map((item) => (
              <div key={item?.id} className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex gap-6">
                  {/* Product Image */}
                  <div className="w-32 h-32 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
                    {item?.image ? (
                      <AppImage
                        src={item?.image}
                        alt={item?.imageAlt || `${item?.title} product image`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Icon name="Package" size={32} className="text-gray-400" />
                      </div>
                    )}
                  </div>

                  {/* Product Details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-1">
                          {item?.title}
                        </h3>
                        {item?.category && (
                          <p className="text-sm text-gray-500">
                            Kategori: {item?.category}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => removeFromCart(item?.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        aria-label="Ürünü kaldır"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>

                    {/* Stock Status */}
                    {item?.trackInventory && (
                      <div className="mb-3">
                        {item?.stockQuantity > 0 ? (
                          <span className="inline-flex items-center gap-1 text-sm text-green-600">
                            <div className="w-2 h-2 bg-green-600 rounded-full" />
                            Stokta mevcut ({item?.stockQuantity} adet)
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-sm text-red-600">
                            <AlertCircle className="h-4 w-4" />
                            Stokta yok
                          </span>
                        )}
                      </div>
                    )}

                    {/* Price and Quantity */}
                    <div className="flex items-center justify-between mt-4">
                      {/* Quantity Controls */}
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-600">Miktar:</span>
                        <div className="flex items-center gap-2 border border-gray-300 rounded-lg">
                          <button
                            onClick={() => handleQuantityUpdate(item?.id, -1)}
                            className="p-2 hover:bg-gray-50 transition-colors"
                            aria-label="Azalt"
                          >
                            <Minus className="h-4 w-4 text-gray-600" />
                          </button>
                          <span className="text-base font-medium text-gray-900 min-w-[2rem] text-center">
                            {item?.quantity}
                          </span>
                          <button
                            onClick={() => handleQuantityUpdate(item?.id, 1)}
                            className="p-2 hover:bg-gray-50 transition-colors"
                            aria-label="Artır"
                          >
                            <Plus className="h-4 w-4 text-gray-600" />
                          </button>
                        </div>
                      </div>

                      {/* Price */}
                      <div className="text-right">
                        <p className="text-sm text-gray-500">Birim Fiyat</p>
                        <p className="text-xl font-bold text-gray-900">
                          {item?.price?.toLocaleString('tr-TR')} ₺
                        </p>
                        <p className="text-sm text-gray-600 mt-1">
                          Toplam: {(item?.price * item?.quantity)?.toLocaleString('tr-TR')} ₺
                        </p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-4 mt-4 pt-4 border-t border-gray-200">
                      <button
                        onClick={() => handleSaveForLater(item)}
                        className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                      >
                        <Heart className="h-4 w-4" />
                        Daha Sonra İçin Kaydet
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Saved For Later Section */}
            {savedForLater?.length > 0 && (
              <div className="mt-8">
                <h2 className="text-xl font-bold text-gray-900 mb-4">
                  Daha Sonra İçin Kaydedilenler ({savedForLater?.length})
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {savedForLater?.map((item) => (
                    <div key={item?.id} className="bg-white rounded-lg shadow-sm p-4">
                      <div className="flex gap-4">
                        <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
                          {item?.image ? (
                            <AppImage
                              src={item?.image}
                              alt={item?.imageAlt || `${item?.title} product image`}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Icon name="Package" size={24} className="text-gray-400" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-bold text-gray-900 line-clamp-2 mb-1">
                            {item?.title}
                          </h4>
                          <p className="text-sm font-semibold text-gray-900 mb-2">
                            {item?.price?.toLocaleString('tr-TR')} ₺
                          </p>
                          <button
                            onClick={() => handleMoveToCart(item)}
                            className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                          >
                            Sepete Taşı
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Order Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-6 sticky top-4">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Sipariş Özeti</h2>

              {/* Coupon Code Input */}
              <div className="mb-6 pb-6 border-b border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Kupon Kodu
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e?.target?.value)}
                    placeholder="Kupon kodunu girin"
                    disabled={!!appliedCoupon}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                  />
                  {appliedCoupon ? (
                    <Button
                      onClick={handleRemoveCoupon}
                      variant="outline"
                      size="sm"
                    >
                      Kaldır
                    </Button>
                  ) : (
                    <Button
                      onClick={handleApplyCoupon}
                      disabled={isApplyingCoupon}
                      size="sm"
                    >
                      {isApplyingCoupon ? 'Kontrol...' : 'Uygula'}
                    </Button>
                  )}
                </div>
                {appliedCoupon && (
                  <div className="mt-2 flex items-center gap-2 text-sm text-green-600">
                    <Tag className="h-4 w-4" />
                    <span>{appliedCoupon?.description} uygulandı</span>
                  </div>
                )}
              </div>

              {/* Price Breakdown */}
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Ara Toplam</span>
                  <span className="font-medium text-gray-900">
                    {cartTotal?.toLocaleString('tr-TR')} ₺
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Kargo</span>
                  <span className="font-medium text-gray-900">
                    {calculateShipping() === 0 ? (
                      <span className="text-green-600">Ücretsiz</span>
                    ) : (
                      `${calculateShipping()?.toLocaleString('tr-TR')} ₺`
                    )}
                  </span>
                </div>
                {cartTotal < 500 && cartTotal > 0 && (
                  <p className="text-xs text-green-600">
                    Ücretsiz kargo için {(500 - cartTotal)?.toLocaleString('tr-TR')} ₺ daha ekleyin!
                  </p>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">KDV (%20)</span>
                  <span className="font-medium text-gray-900">
                    {calculateTax()?.toLocaleString('tr-TR')} ₺
                  </span>
                </div>
                {appliedCoupon && calculateDiscount() > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-green-600">İndirim</span>
                    <span className="font-medium text-green-600">
                      -{calculateDiscount()?.toLocaleString('tr-TR')} ₺
                    </span>
                  </div>
                )}
              </div>

              {/* Total */}
              <div className="flex justify-between items-center pt-6 border-t border-gray-200 mb-6">
                <span className="text-lg font-bold text-gray-900">Toplam</span>
                <span className="text-2xl font-bold text-gray-900">
                  {calculateFinalTotal()?.toLocaleString('tr-TR')} ₺
                </span>
              </div>

              {/* Checkout Button */}
              <Button
                onClick={handleCheckout}
                fullWidth
                size="lg"
                className="mb-4 flex items-center justify-center gap-2"
              >
                <Lock className="h-5 w-5" />
                Güvenli Ödemeye Geç
              </Button>

              {/* Trust Badges */}
              <div className="space-y-2 pt-4 border-t border-gray-200">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Lock className="h-4 w-4 text-green-600" />
                  <span>256-bit SSL Güvenli Ödeme</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Truck className="h-4 w-4 text-blue-600" />
                  <span>3-5 İş Günü İçinde Teslimat</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}